import { Box, Stack } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateTimeUtils } from "@stellar/utils";
import { analyticsApiClient } from "@api/analytics-api-client";
import { AnalyticsApiTypes } from "@stellar/analytics-api-client";
import { AnalyticsBarChart } from "@components/analytics/in-house/analytics-bar-chart";
import { AnalyticsLineChart } from "@components/analytics/in-house/analytics-line-chart";
import { CommonStyles } from "@styles/common-styles";
import { Text, ToggleGroup } from "@flat-ui";
import { DateRangeValue } from "@components/date-range-picker/date-range-picker-types";
import {
  ANALYTICS_CHART_TOGGLE_ITEMS,
  AnalyticsChartType,
} from "@components/analytics/in-house/constants/analytics-constants";
import { useCompanyId } from "@context-providers/company-id-provider";
import {
  buildDailyMetricDataset,
  DailyMetricDataPoint,
  formatDateOnly,
} from "@components/analytics/in-house/utils/analytics-utils";
import { DataViewEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface AnalyticsChartProps {
  /** The selected date range used to fetch and slice the dataset. */
  selectedRange: DateRangeValue;

  /** Metric key from analytics API used for the Y-axis values. */
  metricKey?: AnalyticsApiTypes.MetricKey;

  /** Title shown above the chart. */
  title?: string;

  /** Label shown on the Y-axis. */
  yAxisLabel?: string;

  /** Unit suffix shown in tooltip/series values. */
  seriesUnitLabel?: string;
}

/** Approximate width needed by one date tick label (e.g. "Jun 30"). */
const X_TICK_LABEL_MIN_WIDTH = 64;
/** Conservative fallback label count before first resize measurement arrives. */
const X_TICK_FALLBACK_VISIBLE_LABELS = 7;

/**
 * Renders the in-house analytics chart area using the selected range from parent state.
 * The chart data is derived from mock metrics and filtered by the selected day range.
 */
export function AnalyticsChart({
  selectedRange,
  metricKey = "sheetsAdded",
  title = "Sheets added all projects",
  yAxisLabel = "Number of sheets added",
  seriesUnitLabel = "sheets",
}: AnalyticsChartProps): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const companyId = useCompanyId();
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartType, setChartType] = useState<AnalyticsChartType>("bar");
  const [projectMetrics, setProjectMetrics] = useState<
    AnalyticsApiTypes.GetProjectMetricsRoute["response"] | null
  >(null);

  function handleChartTypeChange(nextChartType: AnalyticsChartType): void {
    if (nextChartType === chartType) {
      return;
    }

    setChartType(nextChartType);
    trackEvent({
      name: DataViewEvents.changeViewMode,
      props: {
        viewType: nextChartType,
      },
    });
  }

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      setChartWidth(entry.contentRect.width);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Keep chart metrics in sync with the selected date range.
  // If the range is incomplete we clear the dataset; otherwise we fetch metrics for that interval.
  useEffect(() => {
    const [startDate, endDate] = selectedRange;
    if (!startDate || !endDate) {
      setProjectMetrics(null);
      return;
    }

    async function fetchProjectMetrics(from: Date, to: Date): Promise<void> {
      const response = await analyticsApiClient.analytics.getProjectMetrics({
        workspaceId: companyId,
        shouldUseMock: true,
        query: {
          from: formatDateOnly(from),
          to: formatDateOnly(to),
        },
      });

      setProjectMetrics(response);
    }

    void fetchProjectMetrics(startDate, endDate);
  }, [companyId, selectedRange]);

  const dataset = useMemo<DailyMetricDataPoint[]>(() => {
    const [startDate, endDate] = selectedRange;
    const totalDays =
      startDate && endDate
        ? Math.abs(
            DateTimeUtils.diffBetweenTwoDatesInDays(endDate, startDate)
          ) + 1
        : 0;

    return buildDailyMetricDataset({
      projectMetrics,
      startDate: startDate ?? new Date(),
      totalDays,
      metricKey,
    });
  }, [projectMetrics, selectedRange, metricKey]);

  const xTickStep = useMemo(() => {
    if (dataset.length <= 1) {
      return 1;
    }

    const maxLabelCount =
      chartWidth > 0
        ? Math.max(1, Math.floor(chartWidth / X_TICK_LABEL_MIN_WIDTH))
        : X_TICK_FALLBACK_VISIBLE_LABELS;

    return Math.max(1, Math.ceil(dataset.length / maxLabelCount));
  }, [chartWidth, dataset.length]);

  return (
    <Box
      ref={chartContainerRef}
      sx={{ border: CommonStyles.Borders.gray200Divider, mt: 1 }}
    >
      <Stack
        alignItems={{ xs: "flex-start", sm: "center" }}
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        px={{ xs: 2, sm: 4 }}
        pt={{ xs: 2, sm: 4 }}
        gap={{ xs: 1.5, sm: 0 }}
      >
        <Text variant="heading20">{title}</Text>

        <ToggleGroup
          items={ANALYTICS_CHART_TOGGLE_ITEMS}
          value={chartType}
          onChange={handleChartTypeChange}
          sxLabels={{
            fontSize: "0.75rem",
          }}
          sxIcons={{
            width: "1rem",
            height: "1rem",
            "& svg": { width: "1rem", height: "1rem" },
          }}
        />
      </Stack>

      {chartType === "bar" ? (
        <AnalyticsBarChart
          dataset={dataset}
          xTickStep={xTickStep}
          yAxisLabel={yAxisLabel}
          seriesUnitLabel={seriesUnitLabel}
        />
      ) : (
        <AnalyticsLineChart
          dataset={dataset}
          xTickStep={xTickStep}
          yAxisLabel={yAxisLabel}
          seriesUnitLabel={seriesUnitLabel}
        />
      )}
    </Box>
  );
}
